import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { showToast } from "../helper/ShowToast";
import * as actions from "../redux/actions/auth"; // adjust your import path
import AuthBackground from '../assets/images/auth-banner.jpg';

export const CompleteRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const state = location.state || {};
  const { email, firstName: gFirstName, lastName: gLastName } = state;

  const auth = useSelector((state) => state.auth);
  const { loading } = auth;

  const [firstName, setFirstName] = useState(gFirstName || "");
  const [lastName, setLastName] = useState(gLastName || "");
  const [userName, setUserName] = useState("");
  const [country, setCountry] = useState(null);
  const [userType, setUserType] = useState("user");
  const [isAgreeforTermAndCOndition, setTermAndCondition] = useState(false);

  const options = useMemo(() => countryList().getData(), []);

  // Better react-select menu styling
  const customSelectStyles = useMemo(
    () => ({
      input: (provided) => ({
        ...provided,
        color: "#fff", //typing/search text color
      }),
      singleValue: (provided) => ({
        ...provided,
        color: "#fff", //selected country color
      }),
      placeholder: (provided) => ({
        ...provided,
        color: "rgba(255,255,255,0.6)", //placeholder visible
      }),
      menu: (provided) => ({
        ...provided,
        maxHeight: "220px",
        overflowY: "auto",
        zIndex: 9999,
      }),
      menuList: (provided) => ({
        ...provided,
        maxHeight: "220px",
        overflowY: "auto",
      }),
    }),
    []
  );

  // Redirect if no email
  useEffect(() => {
    if (!email) navigate("/login");
  }, [email, navigate]);


  const validateForm = () => {
    const fName = firstName.trim();
    const lName = lastName.trim();
    const uname = userName.trim();

    if (!fName || !lName || !uname) {
      showToast("warning", "All fields are required");
      return false;
    }

    if (!country?.value) {
      showToast("warning", "Please select a country");
      return false;
    }
    if (!userType) {
      showToast("warning", "Please select account type");
      return false;
    }

    // Username validation
    const usernameRegex = /^[a-zA-Z0-9._]+$/;

    if (uname.length < 3) {
      showToast("warning", "Username must be at least 3 characters");
      return false;
    }

    if (!usernameRegex.test(uname)) {
      showToast("warning", "Username can only contain letters, numbers, . and _");
      return false;
    }

    if (!isAgreeforTermAndCOndition) {
      showToast("warning", "Please agree to Terms of Service");
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;

    if (!validateForm()) return;

    const payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      userName: userName.trim(),
      country: country.value,
      email,
      userType,
    };

    dispatch(actions.completeRegistration(payload, navigate));
  };

  return (
    <div className="auth-page h-screen flex items-center justify-center">
      <ToastContainer />

      <div className="grid grid-cols-1 md:grid-cols-2 h-full w-full">
        {/* Left Side */}
        <div className="grid-item">
          <div className="left-sec bg-theme_clr1 h-full flex items-center justify-center text-center px-5 py-12 lg:p-[50px] 2xl:p-[69px]">
            <div className="wrapper">
              <div
                type="button"
                className="logo-wrapper mb-6 md:mb-7 xl:mb-[33px] cursor-pointer"
                onClick={() => navigate("/")}
              >
                <img
                  src={require("../assets/images/logo.png")}
                  alt="Logo"
                  className="logo max-w-[145px] 2xl:max-w-[200px] mx-auto"
                />
                <div className="line-bar bg-[#EDECF2] opacity-[15%] h-[1px] w-[64px] mx-auto"></div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="font-manrope w-full max-w-[600px] mx-auto"
              >
                <div className="heading text-white text-xl lg:text-[25px] xl:text-[32px] 2xl:text-[54px] font-medium mb-5 md:mb-6 xl:mb-8 leading-tight">
                  Complete Registration
                </div>

                <div className="input-cover mb-[18px]">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                  />
                </div>

                <div className="input-cover mb-[18px]">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                  />
                </div>

                <div className="input-cover mb-[18px]">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="User Name"
                    value={userName}
                    onChange={(e) =>
                      setUserName(
                        e.target.value
                          .replace(/\s/g, "") //no spaces
                          .toLowerCase() //consistent usernames
                      )
                    }
                    autoComplete="username"
                  />
                </div>

                <div className="input-cover mb-[18px]">
                  <Select
                    className="custom-select"
                    options={options}
                    value={country}
                    onChange={setCountry}
                    styles={customSelectStyles}
                    placeholder="Select country"
                    isSearchable
                    isClearable
                  />
                </div>

                <div className="mb-[22px]">
                  <div className="text-white text-left text-sm md:text-base mb-3 opacity-[80%]">
                    Choose Account Type
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                    {/* Artist / Individual */}
                    <button
                      type="button"
                      onClick={() => setUserType("user")}
                      className={`rounded-2xl border px-4 py-4 text-left transition-all ${userType === "user"
                        ? "border-theme_clr2 bg-white/10 text-white"
                        : "border-white/20 text-white/70 hover:bg-white/5"
                        }`}
                    >
                      <div className="text-base font-medium mb-1">
                        Artist / Individual
                      </div>

                      <div className="text-sm opacity-75 leading-relaxed">
                        For solo artists, bands, creatives, and personal brands.
                      </div>
                    </button>

                    {/* Organization */}
                    <button
                      type="button"
                      onClick={() => setUserType("organization")}
                      className={`rounded-2xl border px-4 py-4 text-left transition-all ${userType === "organization"
                        ? "border-theme_clr2 bg-white/10 text-white"
                        : "border-white/20 text-white/70 hover:bg-white/5"
                        }`}
                    >
                      <div className="text-base font-medium mb-1">
                        Label / Organization
                      </div>

                      <div className="text-sm opacity-75 leading-relaxed">
                        For labels, teams, managers, agencies and businesses.
                      </div>
                    </button>

                  </div>
                </div>

                <div className="check-wrapper flex items-start gap-3 mb-6 2xl:mb-[48px]">
                  <input
                    type="checkbox"
                    checked={isAgreeforTermAndCOndition}
                    onChange={() =>
                      setTermAndCondition(!isAgreeforTermAndCOndition)
                    }
                    id="termsConditionCheck"
                    className="form-check"
                  />

                  <label
                    htmlFor="termsConditionCheck"
                    className="text-white text-left leading-normal"
                  >
                    I agree to the{" "}
                    <Link to="/terms-of-service" className="text-theme_clr2">
                      Terms of Service
                    </Link>{" "}
                    and being sent latest updates related to my account.
                  </label>
                </div>

                <div className="btn-wrapper">
                  <button
                    type="submit"
                    className="loader-button w-full bg-theme_clr2 text-white font-medium flex items-center justify-center gap-[4.5px] rounded-full px-5 py-[11.5px]"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="loader"> </span>
                    ) : (
                      "Become an UpNComer"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right Side */}
        <div className="grid-item">
          <div
            className="right-sec h-full flex items-center justify-center px-5 py-12 lg:p-[50px] 2xl:p-[69px]"
            style={{ backgroundImage: `url(${AuthBackground})` }}
          >
            <div className="overlay-wrapper bg-[#ffffff] opacity-[72%] max-w-[500px] font-manrope rounded-3xl mx-auto p-5 lg:p-6 xl:p-8">
              <div className="flex flex-wrap items-center justify-between mb-4">
                <div className="left">
                  <h5 className="text-base xl:text-lg 2xl:text-xl text-[#707070]">
                    Over
                  </h5>
                  <div className="counter text-[40px] xl:text-[50px] 2xl:text-[67px] font-extrabold leading-tight">
                    3M
                  </div>
                  <div className="stream-on text-base xl:text-lg 2xl:text-xl text-[#707070]">
                    streams on Spotify
                  </div>
                </div>

                <div className="right">
                  <div
                    className="rounded-3xl p-5 lg:p-6"
                    style={{ backgroundColor: "rgba(30, 215, 96, 0.16)" }}
                  >
                    <img
                      src={require("../assets/images/icons/spotify-icon.png")}
                      alt="Spotify Icon"
                      className="block w-[50px] xl:w-[63px]"
                    />
                  </div>
                </div>
              </div>

              <div className="text-black text-base xl:text-lg 2xl:text-xl font-jakarta mb-6 xl:mb-8">
                "What I love most about UpNComer is the clarity it provides. I can
                track my progress across campaigns, finances, and streams—all in
                one place. It’s like having a full music team at my fingertips,
                but with more autonomy."
              </div>

              <div className="author-section flex items-center gap-4">
                <img
                  src={require("../assets/images/karan.png")}
                  alt="Author"
                  className="block w-[60px] 2xl:w-[88px]"
                />
                <div className="author-info capitalize">
                  <div className="name text-lg xl:text-xl 2xl:text-[22px] xl:mb-1">
                    Karan
                  </div>
                  <div className="title text-base xl:text-lg 2xl:text-xl text-[#707070]">
                    Artist
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* End Right */}
      </div>
    </div>
  );
};
